import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { DashboardDataApiRes } from "../oto/retrofit/dashboard/retrofit-dashboard-table/retrofit-dashboard-table.component";
import { RFInputData } from "../oto/retrofit/dialog/metadata/metadata.component";

export type RetrofitTestType =
  | "pre-load"
  | "post-load"
  | "on-road"
  | "fast-charge";

export type ExtendedRetrofitTestType =
  | RetrofitTestType
  | "on-road-pdf"
  | "fast-charge-pdf";

type TestreportData = {
  result: string;
  remark: Record<string, string>;
  unit: string;
  test_name: string;
};

export type EolResults = {
  final_result: string;
  pre_load: string;
  post_load: string;
  fast_charge: string;
  on_road: string;
};

export type VahanData = {
  test_id: string;
  test_date: string;
  vehicle_number: string;
  pkn_id: string;
  front_battery_unique_id: string;
  rear_battery_unique_id: string;
  motor_id_serial_number: string;
  motor_controller_serial_number: string;
  front_bms_unique_id: string;
  rear_bms_unique_id: string;
  bytebeam_id: string;
  eport_id: string;
  eol_results: EolResults;
};

type RetrofitFinalReportData = {
  "pre-load": TestreportData[];
  "post-load": TestreportData[];
  "fast-charge": TestreportData[];
  "on-road": TestreportData[];
  test_data: Record<string, string>;
  summary_results: Record<string, string>;
};

export type RetrofitRawDataTestItem = {
  date: string;
  test_id: string;
  file_name: string;
  raw_data_collection: string;
  test_type: string;
};

@Injectable({
  providedIn: "root",
})
export class RetrofitService {
  constructor(private http: HttpClient) {}

  private EVENT_BASEURL = environment.eventApiUrl;
  private FILE_PROCESSING_BASEURL = environment.eventApiUrl;

  getEpackEolDataByTestRig() {
    const URL = `${this.EVENT_BASEURL}/rf/dashboard/get-epack-data-with-rig-data`;
    return this.http.get<DashboardDataApiRes>(URL, { observe: "response" });
  }

  getPresignedPdfUrl(payload: {
    fileName: string;
    testType: ExtendedRetrofitTestType;
  }) {
    const URL = `${this.EVENT_BASEURL}/rf/dashboard/get-presigned-pdf-url?file_name=${payload.fileName}&test_type=${payload.testType}`;

    return this.http.get<{
      success: boolean;
      data: string;
      message: string;
    }>(URL, { observe: "response" });
  }

  getTestMetaData(testId: string) {
    const URL = `${this.EVENT_BASEURL}/rf/dashboard/get-test-metadata/${testId}`;

    return this.http.get<{
      success: boolean;
      data: RFInputData;
      message: string;
    }>(URL, { observe: "response" });
  }

  getFinalReportData(testId: string) {
    const URL = `${this.EVENT_BASEURL}/rf/dashboard/get-final-pdf-report-data?testId=${testId}`;

    return this.http.get<{
      success: boolean;
      data: RetrofitFinalReportData;
      message: string;
    }>(URL, { observe: "response" });
  }

  getVahanData(vehicleNum: string) {
    const URL = `${this.EVENT_BASEURL}/rf/get-vahan-data?vehicleNumber=${vehicleNum}`;

    return this.http.get<{
      success: boolean;
      data: VahanData;
      message: string;
    }>(URL, { observe: "response" });
  }

  getMetadataReport(testType: RetrofitTestType) {
    const URL = `${this.EVENT_BASEURL}/rf/dashboard/metadata-report?test_type=${testType}`;

    return this.http.get<{
      success: boolean;
      data: any;
      message: string;
    }>(URL, { observe: "response" });
  }

  getRetrofitRawDataTests() {
    const URL = `${this.FILE_PROCESSING_BASEURL}/rf/raw-data/tests`;

    return this.http
      .get<{
        success: boolean;
        message: string | null;
        data: RetrofitRawDataTestItem[];
      }>(URL, { observe: "response" })
      .pipe(
        map((Response) => Response),
        catchError((errorRes) => throwError(errorRes)),
      );
  }

  getRetrofitRawParams(payload: { testId: string; fileName: string }) {
    const URL = `${this.FILE_PROCESSING_BASEURL}/rf/raw-data/test-params`;

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: string[];
      }>(URL, payload, { observe: "response" })
      .pipe(
        map((Response) => Response),
        catchError((errorRes) => throwError(errorRes)),
      );
  }

  getRetrofitRawData(payload: {
    testId: string;
    fileName: string;
    p1?: string;
    p2?: string;
    p3?: string;
  }) {
    const URL = `${this.FILE_PROCESSING_BASEURL}/rf/raw-data/test-data`;

    return this.http
      .post<{
        success: boolean;
        message: string | null;
        data: Record<string, [number, number][]>;
      }>(URL, payload, { observe: "response" })
      .pipe(
        map((Response) => Response),
        catchError((errorRes) => throwError(errorRes)),
      );
  }
}
